import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useCentralAssortmentFilters } from '@retail/my-assortment/context';
import { useFetchCentralAssortment } from '@retail/my-assortment/data-access';
import { CentralAssortmentItem, isMainSupplierItem } from '@retail/my-assortment/types';
import { createItemIdentifiersPredicate } from '@retail/products/utils';
import { QueryOptions } from '@shared/fetch-utils';
import { useMemo } from 'react';

export function useCentralAssortmentItems(queryOptions?: QueryOptions) {
  const { assortmentId, appTenant } = useSelectedOrgUnit();
  const { selectedFilters } = useCentralAssortmentFilters();

  const {
    data: centralAssortmentDTO,
    isFetching,
    isError,
  } = useFetchCentralAssortment({
    assortmentId,
    supplierNumber: selectedFilters.supplierNumber
      ? Number(selectedFilters.supplierNumber)
      : undefined,
    groupNumbers: selectedFilters.productGroupNumbers,
    query: selectedFilters.searchQuery ?? '',
    fromDate: selectedFilters.validFrom,
    toDate: selectedFilters.validTo,
    tagIds: selectedFilters.subAssortmentIds.map((tag) => Number(tag)),
    filterExpiredTags: !selectedFilters.includeExpiredSubAssortments,
    tagFilter:
      selectedFilters.isTagged === 'UNSPECIFIED'
        ? 'ALL'
        : selectedFilters.isTagged === 'YES'
        ? 'TAGGED'
        : 'UNTAGGED',
    keepPreviousData: true,
    ...queryOptions,
  });

  const items = useMemo(
    () =>
      centralAssortmentDTO ? centralAssortmentDTO.items.map(CentralAssortmentItem.fromDto) : [],
    [centralAssortmentDTO]
  );

  const itemIdentifiersPredicate = useMemo(
    () =>
      selectedFilters.itemIdentifiers
        ? createItemIdentifiersPredicate(selectedFilters.itemIdentifiers.identifiers)
        : undefined,
    [selectedFilters.itemIdentifiers]
  );

  const onlyMainSuppliers = appTenant.hasMainSupplier && selectedFilters.fromMainSupplier;

  const filteredItems = useMemo(
    () =>
      items.filter(
        (item) =>
          (!selectedFilters.addedBy || item.createdBy?.iamId === selectedFilters.addedBy) &&
          (!itemIdentifiersPredicate || itemIdentifiersPredicate(item)) &&
          (!onlyMainSuppliers || isMainSupplierItem(item))
      ),
    [items, selectedFilters.addedBy, itemIdentifiersPredicate, onlyMainSuppliers]
  );

  return {
    items,
    filteredItems,
    totalNumberOfItems: centralAssortmentDTO?.totalNumberOfItems ?? 0,
    foundNumberOfItems:
      itemIdentifiersPredicate || onlyMainSuppliers
        ? filteredItems.length
        : centralAssortmentDTO?.foundNumberOfItems,
    isFetching,
    isError,
  };
}
